import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, ViewChild, signal } from '@angular/core';
import { Chart as ChartJs, ChartData, ChartOptions } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { asyncBufferFromUrl, parquetReadObjects } from 'hyparquet';
import { ButtonModule } from 'primeng/button';
import { ChartModule, UIChart } from 'primeng/chart';
import { MessageModule } from 'primeng/message';
import { PanelModule } from 'primeng/panel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ShiftSummaryClient } from '../../../services/nswag/nswagclient';

ChartJs.register(zoomPlugin);

interface ChemicalDosageChartRequest {
  shiftSummaryId: string;
  parquetBlobPath: string | null;
}

interface ChemicalDosageChartPoint {
  timestampUtc: Date;
  canonicalAlias: string;
  value: number | null;
  yAxisIndex: number;
}

interface ChemicalDosageParquetRow {
  timestampUtc?: string | number | Date | null;
  canonicalAlias?: string | null;
  value?: number | null;
  yAxisIndex?: number | null;
}

export interface ChemicalDosageChartViewModel {
  hasData: boolean;
  emptyMessage: string;
  data: ChartData<'line'>;
  options: ChartOptions<'line'>;
}

const NO_SNAPSHOT_MESSAGE = 'No chemical dosage snapshot available for this shift summary.';
const NO_DATA_MESSAGE = 'No chemical dosage data is available for this shift summary.';
const COLORS = [
  '#0f766e',
  '#2563eb',
  '#ea580c',
  '#8b5cf6',
  '#16a34a',
  '#db2777',
  '#b45309',
  '#0284c7',
  '#4f46e5',
  '#0891b2',
  '#65a30d',
  '#dc2626'
];

const timestampLabelFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
});

const defaultChartOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false
  },
  elements: {
    line: {
      tension: 0.2,
      borderWidth: 2
    },
    point: {
      radius: 1.5,
      hitRadius: 8
    }
  },
  plugins: {
    legend: {
      position: 'bottom'
    },
    zoom: {
      limits: {
        x: {
          min: 'original',
          max: 'original'
        },
        yLeft: {
          min: 0
        },
        yRight: {
          min: 0
        }
      },
      pan: {
        enabled: true,
        mode: 'x',
        modifierKey: 'shift'
      },
      zoom: {
        drag: {
          enabled: true
        },
        pinch: {
          enabled: true
        },
        wheel: {
          enabled: true
        },
        mode: 'x'
      }
    }
  },
  scales: {
    x: {
      ticks: {
        autoSkip: true,
        maxRotation: 40,
        minRotation: 40,
        maxTicksLimit: 10
      }
    },
    yLeft: {
      type: 'linear',
      position: 'left',
      beginAtZero: true,
      min: 0,
      title: {
        display: true,
        text: 'GAL/10 BBL'
      }
    },
    yRight: {
      type: 'linear',
      position: 'right',
      beginAtZero: true,
      min: 0,
      grid: {
        drawOnChartArea: false
      },
      title: {
        display: true,
        text: 'LB/10 BBL'
      }
    }
  }
};

const defaultChartData: ChartData<'line'> = {
  labels: [],
  datasets: []
};

function emptyViewModel(message: string): ChemicalDosageChartViewModel {
  return {
    hasData: false,
    emptyMessage: message,
    data: {
      labels: [...(defaultChartData.labels ?? [])],
      datasets: []
    },
    options: defaultChartOptions
  };
}

function normalizeRow(row: ChemicalDosageParquetRow): ChemicalDosageChartPoint | null {
  if (!row?.canonicalAlias) {
    return null;
  }

  const timestampUtc = parseTimestamp(row.timestampUtc);
  if (!timestampUtc) {
    return null;
  }

  const yAxisIndex = row.yAxisIndex === 2 ? 2 : 1;
  const value = row.value == null || Number.isNaN(Number(row.value)) ? null : Number(row.value);

  return {
    timestampUtc,
    canonicalAlias: row.canonicalAlias,
    value,
    yAxisIndex
  };
}

function parseTimestamp(value: string | number | Date | null | undefined): Date | null {
  if (value == null) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatTimestampLabel(timestampUtc: Date): string {
  return timestampLabelFormatter.format(timestampUtc);
}

export function buildChemicalDosageChartViewModel(rows: readonly ChemicalDosageParquetRow[]): ChemicalDosageChartViewModel {
  const normalizedRows = rows
    .map(normalizeRow)
    .filter((row): row is ChemicalDosageChartPoint => !!row)
    .sort((left, right) => left.timestampUtc.getTime() - right.timestampUtc.getTime());

  if (!normalizedRows.length) {
    return emptyViewModel(NO_DATA_MESSAGE);
  }

  const uniqueTimestamps = Array.from(new Set(normalizedRows.map(row => row.timestampUtc.getTime())))
    .sort((left, right) => left - right);

  if (!uniqueTimestamps.length) {
    return emptyViewModel(NO_DATA_MESSAGE);
  }

  const labels = uniqueTimestamps.map(timestamp => formatTimestampLabel(new Date(timestamp)));

  const seriesLookup = new Map<string, { yAxisId: 'yLeft' | 'yRight'; values: Map<number, number | null> }>();
  for (const row of normalizedRows) {
    const axisId = row.yAxisIndex === 2 ? 'yRight' : 'yLeft';
    const existing = seriesLookup.get(row.canonicalAlias);
    if (existing) {
      existing.values.set(row.timestampUtc.getTime(), row.value);
      continue;
    }

    seriesLookup.set(row.canonicalAlias, {
      yAxisId: axisId,
      values: new Map<number, number | null>([[row.timestampUtc.getTime(), row.value]])
    });
  }

  const aliases = Array.from(seriesLookup.keys()).sort((left, right) => left.localeCompare(right));
  const datasets = aliases.map((alias, index) => {
    const series = seriesLookup.get(alias)!;
    return {
      label: alias,
      data: uniqueTimestamps.map(timestamp => series.values.get(timestamp) ?? null),
      borderColor: COLORS[index % COLORS.length],
      backgroundColor: COLORS[index % COLORS.length],
      yAxisID: series.yAxisId,
      spanGaps: true
    };
  });

  return {
    hasData: datasets.length > 0,
    emptyMessage: NO_DATA_MESSAGE,
    data: { labels, datasets },
    options: defaultChartOptions
  };
}

@Component({
  selector: 'app-chemical-dosage-chart',
  standalone: true,
  imports: [CommonModule, PanelModule, ProgressSpinnerModule, MessageModule, ChartModule, ButtonModule],
  templateUrl: './chemical-dosage-chart.html',
  styleUrl: './chemical-dosage-chart.css',
})
export class ChemicalDosageChartComponent implements OnChanges {
  @ViewChild(UIChart) private chartComponent?: UIChart;

  @Input() shiftSummaryId: string | null = null;
  @Input() parquetBlobPath: string | null | undefined = null;

  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);

  private readonly request$ = new BehaviorSubject<ChemicalDosageChartRequest>({
    shiftSummaryId: '',
    parquetBlobPath: null
  });

  readonly viewModel$: Observable<ChemicalDosageChartViewModel> = this.request$.pipe(
    switchMap(request => this.loadViewModel(request)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private readonly shiftSummaryClient: ShiftSummaryClient
  ) { }

  ngOnChanges(_changes: SimpleChanges): void {
    this.request$.next({
      shiftSummaryId: this.shiftSummaryId ?? '',
      parquetBlobPath: this.parquetBlobPath ?? null
    });
  }

  private loadViewModel(request: ChemicalDosageChartRequest): Observable<ChemicalDosageChartViewModel> {
    if (!request.shiftSummaryId || !request.parquetBlobPath) {
      this.loading.set(false);
      this.errorMessage.set(null);
      return of(emptyViewModel(NO_SNAPSHOT_MESSAGE));
    }

    this.loading.set(true);
    this.errorMessage.set(null);

    return this.shiftSummaryClient.getChemicalDosageDownloadUrl(request.shiftSummaryId).pipe(
      switchMap(downloadUrl => from(this.readRowsFromParquet(downloadUrl))),
      map(rows => buildChemicalDosageChartViewModel(rows)),
      tap(() => this.loading.set(false)),
      catchError(error => {
        this.loading.set(false);

        if (this.isNoDataLoadError(error)) {
          this.errorMessage.set(null);
          return of(emptyViewModel(this.getNoDataMessage(error)));
        }

        console.error('Failed loading chemical dosage chart data', error);
        this.errorMessage.set('Unable to load chemical dosage chart data.');
        return of(emptyViewModel(NO_DATA_MESSAGE));
      })
    );
  }

  private async readRowsFromParquet(downloadUrl: string): Promise<ChemicalDosageParquetRow[]> {
    const file = await asyncBufferFromUrl({ url: downloadUrl });
    const rows = await parquetReadObjects({ file });
    return Array.isArray(rows) ? (rows as ChemicalDosageParquetRow[]) : [];
  }

  private isNoDataLoadError(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) {
      return error.status === 400 || error.status === 404;
    }

    const message = this.getErrorMessage(error).toLowerCase();
    return message.includes('parquet file invalid')
      || message.includes('footer != par1');
  }

  private getNoDataMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse && (error.status === 400 || error.status === 404)) {
      return NO_SNAPSHOT_MESSAGE;
    }

    return NO_DATA_MESSAGE;
  }

  private getErrorMessage(error: unknown): string {
    if (!error) {
      return '';
    }

    if (error instanceof Error) {
      return error.message ?? '';
    }

    return String(error);
  }

  resetZoom(): void {
    const chart = this.chartComponent?.chart;
    if (!chart) {
      return;
    }

    const labelsCount = chart.data.labels?.length ?? 0;
    const chartWithZoom = chart as ChartJs<'line'> & {
      resetZoom?: () => void;
      zoomScale?: (scaleId: string, range: { min: number; max: number }, mode?: 'none' | 'default' | 'resize') => void;
    };

    chartWithZoom.resetZoom?.();
    if (labelsCount > 0) {
      chartWithZoom.zoomScale?.('x', { min: 0, max: labelsCount - 1 }, 'none');
    }
    chart.update('none');
  }

}
